/**
 * `showApiError()` — the ONE way a failed API call reaches a fan.
 *
 * A module-level pub/sub, not a store and not context: any code path can raise
 * an error, including a react-query `onError` that has no component to hang a
 * hook off. Client state on this platform is URL search params + React context
 * + module pub/sub, and this is the third of those.
 *
 * What this is NOT for:
 *  - A required field left empty. That is inline validation under the field.
 *    Sending it here would show a fan a support path over their own unfinished
 *    typing.
 *  - Success or neutral info. This product has no toasts and no snackbars, for
 *    success or for error, on any platform including Android. An outcome is
 *    announced by something the fan reads at their own pace and dismisses
 *    themselves: `InlineBanner` or `SavedNote` from `@sportsmet/ui`, a
 *    screen-level confirmation state, or a sheet.
 */
import {
  ERROR_CODES,
  errorClassCopy,
  type ErrorCode,
  errorCodeClass,
  type ErrorCopyContext,
  resolveContextCopy,
} from "@sportsmet/errors";

export type typeErrorModalEvent = {
  /**
   * The machine code, shown verbatim in the modal so a fan can paste it into a
   * support message. `null` for the offline case, which never reached a server
   * and therefore has no code to report.
   */
  code: string | null;
  /** One plain sentence, from the shared catalog. Never an API's raw message. */
  message: string;
};

type typeListener = (event: typeErrorModalEvent) => void;

const listeners = new Set<typeListener>();

// The bus records the signature and the stamp of what it published so a dismissal
// can be matched against a repeat.
let currentSignature: string | null = null;
let currentAnsweredAt: number | undefined;
let dismissedSignature: string | null = null;
let dismissedAnsweredAt: number | undefined;

// First-seen stamps for react-query reads live here, not in useRef, so a
// remount on a cached answer reuses the stamp the dismissal latch matched.
const firstSeenAtByQueryError = new Map<string, number>();

function queryErrorStampKey(queryIdentity: string, errorCode: string): string {
  return `${queryIdentity}\0${errorCode}`;
}

/** Stable identity for a react-query read that may raise through showQueryApiError. */
export function queryApiErrorIdentity(
  ...parts: readonly (string | number | boolean | null | undefined)[]
): string {
  return JSON.stringify(parts);
}

function forgetQueryApiErrorIdentity(queryIdentity: string): void {
  const prefix = `${queryIdentity}\0`;
  for (const key of firstSeenAtByQueryError.keys()) {
    if (key.startsWith(prefix)) firstSeenAtByQueryError.delete(key);
  }
}

/**
 * Drop first-seen stamp so the next arrival is a new episode. Call before a
 * fan-initiated refetch from a Try again action or the error modal path.
 */
export function forgetQueryApiError(
  queryIdentity: string,
  errorCode: string,
): void {
  firstSeenAtByQueryError.delete(queryErrorStampKey(queryIdentity, errorCode));
}

/**
 * Raise from a react-query render effect. Records first-seen stamp per query
 * identity and error code in a module map so remounts reuse the latch and poll
 * ticks that only bump dataUpdatedAt do not mint a new stamp.
 */
export function showQueryApiError(
  source: unknown,
  queryIdentity: string,
  errorCode: string | null,
  dataUpdatedAt: number,
  context?: ErrorCopyContext,
): boolean {
  if (errorCode === null) {
    forgetQueryApiErrorIdentity(queryIdentity);
    return false;
  }
  const stampKey = queryErrorStampKey(queryIdentity, errorCode);
  let seenAt = firstSeenAtByQueryError.get(stampKey);
  if (seenAt === undefined) {
    seenAt = dataUpdatedAt;
    firstSeenAtByQueryError.set(stampKey, seenAt);
  }
  return showApiError(source, context, seenAt);
}

/** Clears module first-seen stamps between tests. */
export function resetQueryApiErrorStamps(): void {
  firstSeenAtByQueryError.clear();
}

export function subscribeToErrorModal(listener: typeListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function signatureOf(parts: {
  code: string | null;
  context: ErrorCopyContext | undefined;
  ref: string | undefined;
}): string {
  return JSON.stringify([
    parts.code ?? "",
    parts.context ?? "",
    parts.ref ?? "",
  ]);
}

function isAlreadyDismissed(
  signature: string,
  answeredAt: number | undefined,
): boolean {
  if (dismissedSignature === null) return false;
  if (answeredAt === undefined || dismissedAnsweredAt === undefined) {
    return false;
  }
  if (answeredAt !== dismissedAnsweredAt) return false;
  return signature === dismissedSignature;
}

function emit(
  event: typeErrorModalEvent,
  signature: string,
  answeredAt?: number,
): void {
  if (isAlreadyDismissed(signature, answeredAt)) return;
  currentSignature = signature;
  currentAnsweredAt = answeredAt;
  for (const listener of listeners) listener(event);
}

const KNOWN_CODES = new Set<string>(ERROR_CODES);

function isKnownCode(code: string): code is ErrorCode {
  return KNOWN_CODES.has(code);
}

/**
 * Maps a code to its one sentence.
 *
 * `context` is an optional call-site tag. The sentence it names still comes
 * from `@sportsmet/errors`, never from a literal here; when the context
 * resolves no sentence for this code, this falls through to the class copy
 * below exactly as it would with no context at all.
 *
 * An unrecognized code has no class, so it falls back to the `internal`
 * sentence rather than a hand-written one-off — the fan is told the same thing
 * they would be told about any other server-side fault, and the code itself is
 * still on screen for support. That fallback is for genuinely unknown codes; a
 * new code shipped by a service gets a real class in `@sportsmet/errors` in the
 * same change.
 */
export function copyForCode(code: string, context?: ErrorCopyContext): string {
  if (context !== undefined) {
    const contextCopy = resolveContextCopy({ context, code });
    if (contextCopy !== null) return contextCopy;
  }
  if (isKnownCode(code)) return errorClassCopy[errorCodeClass[code]];
  return errorClassCopy.internal;
}

type typeEnvelopeLike = {
  error?: { code?: unknown } | null;
  data?: { errorCode?: unknown } | null;
};

/** Pulls the code out of either envelope shape, or null when there is none. */
export function extractErrorCode(source: unknown): string | null {
  if (typeof source !== "object" || source === null) return null;
  const envelope = source as typeEnvelopeLike;

  const fromError = envelope.error?.code;
  if (typeof fromError === "string" && fromError !== "") return fromError;

  const fromData = envelope.data?.errorCode;
  if (typeof fromData === "string" && fromData !== "") return fromData;

  return null;
}

function readRef(source: unknown): string | undefined {
  if (typeof source !== "object" || source === null) return undefined;
  const details = (source as { error?: { details?: unknown } | null }).error
    ?.details;
  if (typeof details !== "object" || details === null) return undefined;
  const ref = (details as { ref?: unknown }).ref;
  return typeof ref === "string" ? ref : undefined;
}

/**
 * The sentence for a refusal envelope, or `null` when this refusal has no
 * sentence of its own and the screen should keep using its own generic line.
 *
 * Only `FILE_TOO_LARGE` resolves here, through the `photoUpload` context in
 * `@sportsmet/errors` (ISSUE-839). Every other code stays `null` on purpose:
 * an upload hook must not start swapping a screen's own wording for class copy
 * on refusals that read fine today. That is why this is not `copyForCode`,
 * which always falls through to the class sentence.
 */
export function photoUploadRefusalMessage(source: unknown): string | null {
  const code = extractErrorCode(source);
  if (code === null) return null;
  return resolveContextCopy({ context: "photoUpload", code });
}

/**
 * Shows the error modal for an SDK result.
 *
 * Returns `true` when it raised a modal — so a caller can write the happy path
 * as `if (showApiError(result)) return;` without duplicating the envelope check.
 * A result with no error raises nothing and returns `false`.
 */
export function showApiError(
  source: unknown,
  context?: ErrorCopyContext,
  answeredAt?: number,
): boolean {
  const code = extractErrorCode(source);
  if (code === null) return false;
  emit(
    { code, message: copyForCode(code, context) },
    signatureOf({ code, context, ref: readRef(source) }),
    answeredAt,
  );
  return true;
}

/**
 * The request never reached a server. There is no code, so the fan is told the
 * connection is the problem rather than being handed a server fault they did
 * not cause. Wire this to react-query's `onError` branch for a network failure.
 *
 * The sentence is the shared catalog's `offline` class copy, the same one the
 * SDK's synthesized `NETWORK_UNREACHABLE` envelope resolves to, so this path
 * and the staff panel say the one sentence for the one event.
 */
export function showOfflineError(): void {
  emit(
    { code: null, message: errorClassCopy.offline },
    signatureOf({ code: null, context: undefined, ref: undefined }),
  );
}

/** Records what was closed so a repeat raise of the same answer can be dropped. */
export function dismissApiError(): void {
  dismissedSignature = currentSignature;
  dismissedAnsweredAt = currentAnsweredAt;
  currentSignature = null;
  currentAnsweredAt = undefined;
}
